import { useEffect, useMemo, useRef, useState } from 'react';

import { BLURRED_TRANSFER_PLAYERS } from '@components/manual-team-entry/template-players';
import { fetchPlayersByOptaIds } from '@lib/api/gytr-portraits';
import type { Player } from '@api-service/players';

/** Fisher-Yates over a copy; the input list is a module constant. */
const shuffled = <T>(items: T[]): T[] => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

/**
 * Picks the players whose portraits sit, blurred, on the IN side of the locked
 * transfer cards (FFH-2409).
 *
 * These are decoys, not the recommendation. The solver's IN player is the
 * product and is never sent to the client, so what is blurred here is a random
 * pick from a curated list — which is why the real name sitting in the DOM
 * under a CSS blur is not a leak of anything. Do not "fix" that by wiring the
 * actual suggestion in.
 *
 * The OUT players on the current cards are excluded, so no card ever offers
 * back, however hazily, the player it is moving on.
 *
 * Returns fewer than `count` — including none at all — whenever the fetch has
 * not landed or failed. Every caller has to render that case; a decoy is
 * decoration and must never hold up the card.
 */
export const useBlurredTransferPlayers = (
  outPlayerIds: string[],
  count: number
): Player[] => {
  const [candidates, setCandidates] = useState<Player[]>([]);
  /**
   * Whether the one fetch this hook makes has been started. A ref rather than
   * state because the list asked for is a module constant: nothing about a
   * later render can change the request, so re-running it could only ever
   * repeat itself.
   */
  const hasRequested = useRef(false);

  useEffect(() => {
    if (hasRequested.current) return;
    hasRequested.current = true;

    // Deliberately not cancelled on cleanup, matching the portrait fetch on the
    // transfer step: this is reference data keyed by id, so an answer arriving
    // late is still the right answer.
    fetchPlayersByOptaIds(BLURRED_TRANSFER_PLAYERS)
      .then(setCandidates)
      .catch(() => {
        // Left marked as requested. A retry loop is not worth it for
        // decoration, and the cards degrade to a plain placeholder circle.
      });
  }, []);

  return useMemo(() => {
    const excluded = new Set(outPlayerIds);
    // Fetched by Opta id but compared by UUID: `outPlayerIds` are season UUIDs,
    // and matching them against the Opta ids asked for would silently exclude
    // nobody.
    const available = candidates.filter((player) => !excluded.has(player.id));

    // Shuffled once and sliced, so the cards cannot draw the same face twice.
    // Re-drawn whenever the cards change — in practice once, when the solve
    // lands and replaces the pending card — which is also what gives a
    // returning visitor a different three.
    return shuffled(available).slice(0, count);
  }, [candidates, outPlayerIds, count]);
};
